import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { PartnerMapState } from "./types";
import { fetchStoreGeo, fetchStoreStatistics } from "./partner-map-thunk";
import { ErrorType } from "@/shared/types";
import { IPartnerStoreGeo } from "@/shared/api/partners";

const currentDate = new Date()

const initialState: PartnerMapState = {
    storeGeo: [],
    storeGeoError: null,
    isStoreGeoLoading: false,
    isStoreGeoFirstLoad: true,
    storeStatistics: {},
    storeStatisticsError: null,
    isStoreStatisticsLoading: false,
    isStoreStatisticsFirstLoad: true,
    storeStatisticsLatestRequestId: null,
    startDate: new Date(currentDate.getFullYear(), currentDate.getMonth(), 1),
    endDate: new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1),
    selectedPartner: null,
    selectedStore: null,
    isInspectStoreModalOpen: false
}

const partnerMapSlice = createSlice({
    name: 'partnerMap',
    initialState,
    reducers: {
        setStartDate: (state, action: PayloadAction<Date>) => {
            state.startDate = action.payload
            state.isStoreStatisticsLoading = false
            state.storeStatistics = {}
            state.storeStatisticsError = null
            state.storeStatisticsLatestRequestId = null
        },
        setEndDate: (state, action: PayloadAction<Date>) => {
            state.endDate = action.payload
            state.isStoreStatisticsLoading = false
            state.storeStatistics = {}
            state.storeStatisticsError = null
            state.storeStatisticsLatestRequestId = null
        },
        setSelectedPartner: (state, action: PayloadAction<string | null>) => {
            state.selectedPartner = action.payload
            state.isStoreStatisticsLoading = false
            state.storeStatistics = {}
            state.storeStatisticsError = null
            state.storeStatisticsLatestRequestId = null
        },
        setSelectedStore: (state, action: PayloadAction<IPartnerStoreGeo>) => {
            state.selectedStore = action.payload
        },
        openInspectStoreModal: (state, action: PayloadAction<IPartnerStoreGeo>) => {
            state.isInspectStoreModalOpen = true
            state.selectedStore = action.payload
        },
        closeInspectStoreModal: (state) => {
            state.isInspectStoreModalOpen = false
        }
    },
    extraReducers(builder) {
        builder
        .addCase(fetchStoreGeo.pending, (state) => {
            state.isStoreGeoLoading = true
            state.storeGeoError = null
        })
        .addCase(fetchStoreGeo.fulfilled, (state, action) => {
            state.isStoreGeoLoading = false
            state.isStoreGeoFirstLoad = false
            state.storeGeo = action.payload
            state.storeGeoError = null
        })
        .addCase(fetchStoreGeo.rejected, (state, action) => {
            state.storeGeoError = action.payload as ErrorType ?? null
            state.isStoreGeoLoading = false
        })
        .addCase(fetchStoreStatistics.pending, (state, action) => {
            state.isStoreStatisticsLoading = true
            state.storeStatistics = {}
            state.storeStatisticsError = null
            state.storeStatisticsLatestRequestId = action.meta.requestId
        })
        .addCase(fetchStoreStatistics.fulfilled, (state, action) => {
            if (state.storeStatisticsLatestRequestId !== action.meta.requestId) return
            state.isStoreStatisticsLoading = false
            state.isStoreStatisticsFirstLoad = false
            state.storeStatistics = action.payload
            state.storeStatisticsError = null
        })
        .addCase(fetchStoreStatistics.rejected, (state, action) => {
            if (state.storeStatisticsLatestRequestId !== action.meta.requestId) return
            state.isStoreStatisticsLoading = false
            state.storeStatisticsError = action.payload as ErrorType ?? null
        })
    },
})

export default partnerMapSlice.reducer
export const { setEndDate, setSelectedPartner, setStartDate, setSelectedStore, openInspectStoreModal, closeInspectStoreModal } = partnerMapSlice.actions